// Local shim for `@s-ui/pde`.
//
// Aliased in `vite.config.js` via `resolve.alias` with an exact regex so any
// `import ... from '@s-ui/pde'` in the app is routed here. Sub-paths like
// `@s-ui/pde/lib/adapters/optimizely` bypass the alias and reach the real
// package unchanged, which is also how this file loads the base module below.
//
// The reason for the shim: `@s-ui/pde`'s server strategy does NOT default
// `queryString` to the request URL, while the browser strategy defaults to
// `document.location.search`. That asymmetry makes `useDecision(...)` on the
// server ignore `?suipde_<flag>=on/off/…` (and any other decision detail that
// depends on the URL) while the same call on the client honours it — a
// guaranteed hydration mismatch that shows up as a visible flash for any
// component gated by a feature flag under QA forcing. Overriding `useDecision`
// to always pass `location.search` closes the gap for every consumer with a
// single line of business-code impact (this file).
import {useSyncExternalStore} from 'react'
import {useLocation} from 'react-router-dom'

import {
  Decision,
  Experiment,
  Feature,
  getDecision,
  PDE,
  PdeContext,
  useDecision as baseUseDecision,
  useDecisionCallback,
  useExperiment,
  useFeature
} from '@s-ui/pde/lib/index.js'

export {Decision, Experiment, Feature, getDecision, PDE, PdeContext, useDecisionCallback, useExperiment, useFeature}

// Decisions SSR already took for this page, keyed by flag (see contextFactory/experimentationContext.js).
const getHydratedDecision = name =>
  typeof window === 'undefined' ? undefined : window.__INITIAL_CONTEXT_VALUE__?.pdeDecisions?.[name]

const NEVER_CHANGES = () => () => {}
const isSameValue = (one, other) => (one ?? null) === (other ?? null)

export const useDecision = (name, options = {}) => {
  const location = useLocation()
  const decision = baseUseDecision(name, {
    queryString: location.search,
    ...options
  })

  // The client decides again from its own datafile, and that download does not always land — ad
  // blockers routinely block cdn.optimizely.com — in which case every flag reads as disabled. A
  // flag that drives markup then hydrates into DOM the server never sent, React throws #418 and
  // tears down the whole root: that is how the ads grid, which derives the tall ad slot from
  // `ff_web_buyer_ads_grid_advertisement_first_row`, took down every listing page.
  //
  // React renders `getServerSnapshot` on the server and for the hydration pass only, so the
  // server's decision covers exactly the render that has to match the markup and the live one
  // takes over right after. A component mounted by a later client navigation never sees it, which
  // matters because callers pass per-page `attributes` that the recorded decision cannot know.
  const hydratedDecision = getHydratedDecision(name)
  const divergesFromServer = Boolean(
    hydratedDecision &&
      !(
        isSameValue(hydratedDecision.enabled, decision?.enabled) &&
        isSameValue(hydratedDecision.variationKey, decision?.variationKey)
      )
  )
  // Both snapshots agree unless the two sides diverge, so the usual case costs no extra render.
  const isHydratingDivergence = useSyncExternalStore(
    NEVER_CHANGES,
    () => false,
    () => divergesFromServer
  )

  return isHydratingDivergence ? {...decision, ...hydratedDecision} : decision
}
